<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import CountryService from "@/services/CountryService";
import AuthLayout from "@/components/layouts/AuthLayout.vue";
import axios from "@/axios";
import Swal from "sweetalert2";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
library.add(faCircle);

const router = useRouter();
const authStore = useAuthStore();
const checkingAuth = ref(true);
const isLoading = ref(true);

const membershipData = ref({
  id: "",
  identifier: "",
  status: "Activa",
  total_points: 0,
  current_level: null,
  level_progress: null,
});

const countryBenefits = ref([
  "Acceso especial a eventos y lanzamientos",
  "Participación en los mejores hike de la región",
  "Acumulación de puntos",
  "Descuentos exclusivos",
  "Merch",
]);

const countryCode = ref("");
const countryName = ref("Tu país");

const goToDashboard = () => {
  router.push("/dashboard");
};

const fetchQRData = async () => {
  try {
    const response = await axios.get("/v1/membership/activated");

    if (response.data && response.data.qr) {
      membershipData.value.id = response.data.qr.identifier || "";
    } else {
      console.warn("No se encontró información del QR activado");
    }
  } catch (error) {
    console.error("Error obteniendo datos del QR activado:", error);
  }
};

const fetchMembershipData = async () => {
  isLoading.value = true;

  try {
    const membershipResponse = await axios.get("/v1/membership/my-info");

    if (membershipResponse.data.has_membership) {
      membershipData.value = {
        ...membershipData.value,
        total_points: membershipResponse.data.total_points,
        current_level: membershipResponse.data.current_level,
        level_progress: membershipResponse.data.level_progress,
      };

      await fetchQRData();

      await fetchCountryInfo();
    } else {
      Swal.fire({
        icon: "warning",
        title: "Membresía no activada",
        text: "No tienes una membresía activa. Te redirigiremos para activarla.",
        toast: true,
        position: "top-right",
        timer: 3000,
        showConfirmButton: false,
      });

      setTimeout(() => {
        router.push("/activate-membership");
      }, 1500);
    }
  } catch (error) {
    console.error("Error obteniendo datos de membresía:", error);

    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No pudimos obtener la información de tu membresía. Inténtalo más tarde.",
      toast: true,
      position: "top-right",
      timer: 3000,
      showConfirmButton: false,
    });
  } finally {
    isLoading.value = false;
  }
};

const fetchCountryInfo = async () => {
  try {
    const user = authStore.user;

    if (!user || !user.country) {
      console.warn("No se encontró información del país del usuario");
      return;
    }
    const countries = await CountryService.getAvailableCountries();
    const userCountry = countries.find(
      (country) => country.id === user.country.id
    );
    if (!userCountry) {
      console.warn("No se encontró información del país del usuario");
      return;
    }


    countryCode.value = userCountry ? userCountry.code : "";
    countryName.value = userCountry ? userCountry.name : "Tu país";

    const levelsResponse = await axios.get("/v1/membership/levels");

    if (levelsResponse.data.levels && levelsResponse.data.levels.length > 0) {
      const firstLevel = levelsResponse.data.levels[0];

      if (firstLevel.benefits && Array.isArray(firstLevel.benefits)) {
        countryBenefits.value = userCountry.general_benefits;
      }
    }
  } catch (error) {
    console.error("Error obteniendo información del país:", error);
  }
};

onMounted(async () => {
  try {
    const isAuthenticated = await authStore.checkAuth();

    if (!isAuthenticated) {
      Swal.fire({
        icon: "warning",
        title: "Sesión no iniciada",
        text: "Debes iniciar sesión para ver tu membresía",
        toast: true,
        position: "top-right",
        timer: 3000,
        showConfirmButton: false,
      });

      setTimeout(() => {
        router.push("/login");
      }, 1500);
      return;
    }

    await fetchMembershipData();
  } catch (error) {
    console.error("Error en inicialización:", error);
  } finally {
    checkingAuth.value = false;
  }
});
</script>

<template>
  <div
    v-if="checkingAuth || isLoading"
    class="min-h-screen flex justify-center items-center bg-gray-100"
  >
    <div class="loader-pulse"></div>
  </div>
  <AuthLayout v-else>
    <template #title>
      <h1 class="text-4xl font-extrabold text-white mb-2 drop-shadow-lg">
        Bienvenido! Ya eres parte de Hike Society.
      </h1>
      <p class="text-white/80 text-lg mb-6 max-w-md mx-auto">
        Ahora, ya puedes inscribirte a tu primer Hike y en el lugar del evento
        se te entregara tu Kit de bienvenida
      </p>
    </template>
 
    <!-- Nivel de Membresía Mejorado (Arriba) -->
    <div
      v-if="membershipData.current_level"
      class="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 mx-auto max-w-3xl"
    >
      <div class="flex flex-col items-center justify-center">
        <div
          class="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mb-4"
        >
          <span class="text-4xl font-bold text-white">{{
            membershipData.current_level.level_identifier || "1"
          }}</span>
        </div>
        <h2 class="text-3xl font-bold text-white mb-2">
          {{ membershipData.current_level.name }}
        </h2>

        <p class="text-xl text-white/90 mb-6">
          <span class="text-3xl font-bold">{{
            membershipData.total_points
          }}</span>
          puntos acumulados
        </p>

        <!-- Barra de progreso -->
        <div
          v-if="
            membershipData.level_progress &&
            membershipData.level_progress.next_level
          "
          class="w-full max-w-lg"
        >
          <div class="flex justify-between text-sm text-white/80 mb-2">
            <span>{{ membershipData.current_level.name }}</span>
            <span>{{ membershipData.level_progress.next_level.name }}</span>
          </div>
          <div class="w-full bg-gray-200/30 rounded-full h-3 mb-2">
            <div
              class="bg-green-500 h-3 rounded-full shadow-lg"
              :style="`width: ${membershipData.level_progress.percentage}%`"
            ></div>
          </div>
          <p class="text-center text-white/80 mt-2">
            {{ membershipData.level_progress.points_needed }}
            puntos más para el siguiente nivel
          </p>
        </div>
      </div>
    </div>

    <!-- Contenido Principal (ID y Beneficios en dos columnas) -->
    <main
      class="flex flex-col lg:flex-row gap-8 justify-center max-w-6xl mx-auto px-4"
    >
      <!-- Columna Izquierda: ID de Membresía -->
      <div class="w-full lg:w-1/2 text-white">
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-8 h-full">
          <h2 class="text-2xl font-bold mb-6 text-center">Tu Membresía</h2>

          <!-- ID de membresía -->
          <div class="mb-8 text-center">
            <p class="text-white/90 mb-2">ID de membresía:</p>
            <p
              class="text-2xl font-mono tracking-wider bg-white/5 py-3 px-4 rounded-lg"
            >
              {{ membershipData.id || "Pendiente" }}
            </p>
            <div
              class="inline-block bg-white text-black px-6 py-2 rounded-full font-semibold my-4"
            >
              Estado: {{ membershipData.status }}
            </div>
          </div>

          <!-- Fecha de activación -->
          <div class="text-center">
            <p class="text-white/90 mb-2">Fecha de activación:</p>
            <p class="text-xl">{{ new Date().toLocaleDateString() }}</p>
          </div>
        </div>
      </div>

      <!-- Columna Derecha: Beneficios del País -->
      <div class="w-full lg:w-1/2 text-white">
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-8 h-full">
          <h2 class="text-2xl font-bold mb-6 text-center">Mis Beneficios</h2>
          <ul class="text-left text-xl space-y-4 max-w-md mx-auto">
            <li
              v-for="(benefit, index) in countryBenefits"
              :key="index"
              class="flex items-start gap-2"
            >
            <font-awesome-icon :icon="['fas', 'circle']" class="text-white" />
              <span>{{ benefit }}</span>
            </li>
          </ul>
        </div>
      </div>
    </main>

    <template #footer-links>
      <div class="max-w-md mx-auto text-center mt-8">
        <button
          @click="goToDashboard"
          class="bg-white/10 hover:bg-white text-white hover:text-black px-8 py-3 rounded-full font-semibold transition-colors mx-auto text-center text-xl border border-white/30"
        >
          Continuar
        </button>
      </div>
    </template>
  </AuthLayout>
</template>

<style scoped>
/* Efecto de animación de entrada */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

main > div {
  animation: fadeInUp 0.6s ease-out;
}

/* Efecto hover en los items de beneficios */
li {
  transition: transform 0.2s ease;
}

li:hover {
  transform: translateX(5px);
}

/* Animación para la barra de progreso */
.bg-green-500 {
  transition: width 1.5s ease-in-out;
}
</style>
